import React, { useState, useEffect } from "react";

// Range bounds for the outbound flight time, in minutes. Olympic's network is short/med-haul
// (~2h–5h), so 1h–6h with 15-minute steps covers it with headroom.
const MIN = 60;
const MAX = 360;
const STEP = 15;

const fmt = (m) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm === 0 ? `${h}h` : `${h}h ${mm.toString().padStart(2, "0")}m`;
};

// Two-handle flight-time range. Reads/writes two URL params via the shared filter events
// (getValue / setOption), so it round-trips in shareable search URLs like every other filter.
// Local state tracks the drag; the committed value (which triggers a refetch) is only written
// on pointer/key release, so dragging doesn't fire a search per step.
const FilterFlightDuration = ({ minKey, maxKey, events }) => {
    const committedMin = parseInt(events.getValue(minKey), 10);
    const committedMax = parseInt(events.getValue(maxKey), 10);

    const [lo, setLo] = useState(Number.isFinite(committedMin) ? committedMin : MIN);
    const [hi, setHi] = useState(Number.isFinite(committedMax) ? committedMax : MAX);

    // Keep local state in sync if the URL params change elsewhere (e.g. reset filters, back button).
    useEffect(() => {
        setLo(Number.isFinite(committedMin) ? committedMin : MIN);
        setHi(Number.isFinite(committedMax) ? committedMax : MAX);
    }, [committedMin, committedMax]);

    const commit = (nextLo, nextHi) => {
        // Only persist a bound that actually narrows the full range, so an untouched slider
        // adds no query param (and never hides offers).
        if (nextLo > MIN) events.setOption(minKey, nextLo);
        else events.removeOption(minKey);
        if (nextHi < MAX) events.setOption(maxKey, nextHi);
        else events.removeOption(maxKey);
    };

    const onLo = (v) => setLo(Math.min(v, hi));   // handles can't cross
    const onHi = (v) => setHi(Math.max(v, lo));

    const pct = (v) => ((v - MIN) / (MAX - MIN)) * 100;

    return (
        <div className="px-1">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{fmt(lo)}</span>
                <span>{fmt(hi)}</span>
            </div>
            {/* One track, two thumbs. The inputs are stacked on top of each other; the CSS makes the
                inputs transparent to the pointer and only the thumbs clickable, so both handles remain
                grabbable despite overlapping. */}
            <div className="range-dual">
                <div className="range-dual__track" />
                <div
                    className="range-dual__fill"
                    style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
                />
                <label className="sr-only" htmlFor="flight-duration-min">Minimum flight time</label>
                <input
                    id="flight-duration-min"
                    type="range"
                    min={MIN}
                    max={MAX}
                    step={STEP}
                    value={lo}
                    onChange={(e) => onLo(parseInt(e.target.value, 10))}
                    onMouseUp={() => commit(lo, hi)}
                    onTouchEnd={() => commit(lo, hi)}
                    onKeyUp={() => commit(lo, hi)}
                />
                <label className="sr-only" htmlFor="flight-duration-max">Maximum flight time</label>
                <input
                    id="flight-duration-max"
                    type="range"
                    min={MIN}
                    max={MAX}
                    step={STEP}
                    value={hi}
                    onChange={(e) => onHi(parseInt(e.target.value, 10))}
                    onMouseUp={() => commit(lo, hi)}
                    onTouchEnd={() => commit(lo, hi)}
                    onKeyUp={() => commit(lo, hi)}
                />
            </div>
            <p className="text-xs text-gray-400 mt-2">Estimated outbound flight time</p>
        </div>
    );
};

export default FilterFlightDuration;
